import axios from 'axios';
import { SERVER_URL } from './api';

/**
 * The axios instance the external participant portal uses.
 *
 * Separate from utils/api.js, and it has to be. That instance sends the
 * employee token from localStorage `token`, and on any 401 it clears the
 * session and redirects to /login — which for a vendor whose link has expired
 * would mean being thrown at an employee login screen they can never get past,
 * with no explanation of what went wrong.
 *
 * This one carries the portal token, under its own storage key so an employee
 * and a vendor using the same browser cannot overwrite each other, and reports
 * failures back to the page instead of navigating away from it.
 */

const PORTAL_TOKEN_KEY = 'portalToken';

/**
 * Keyed by invite token, not stored as a single value.
 *
 * One person can hold links to more than one conversation — a vendor working on
 * two projects gets two invitations — and a single slot would silently log them
 * out of the first the moment they opened the second.
 */
const store = () => {
    try {
        return JSON.parse(localStorage.getItem(PORTAL_TOKEN_KEY) || '{}');
    } catch {
        return {};
    }
};

export const getPortalToken = (inviteToken) => store()[inviteToken] || null;

export const setPortalToken = (inviteToken, sessionToken) => {
    try {
        localStorage.setItem(
            PORTAL_TOKEN_KEY,
            JSON.stringify({ ...store(), [inviteToken]: sessionToken })
        );
    } catch {
        // A browser with storage disabled still works — the session simply
        // lasts until the tab is closed, which for a one-off vendor reply is
        // an acceptable outcome and better than a blank page.
    }
};

export const clearPortalToken = (inviteToken) => {
    try {
        const next = store();
        delete next[inviteToken];
        localStorage.setItem(PORTAL_TOKEN_KEY, JSON.stringify(next));
    } catch { /* nothing to clear */ }
};

const portalApi = axios.create({
    baseURL: `${SERVER_URL}/api/portal`,
    headers: { 'Content-Type': 'application/json' }
});

/**
 * The active session token, set once by the page after joining.
 *
 * Held in a module variable rather than read from storage on every request, so
 * that a page opened with one invite link cannot pick up a different link's
 * session out of the shared store.
 */
let active = null;

// Not a React hook, despite what a `use` prefix would have implied: naming it
// useSession made eslint's rules-of-hooks reject every call from inside a
// callback and fail the production build.
export const setActiveSession = (sessionToken) => { active = sessionToken; };

portalApi.interceptors.request.use((config) => {
    if (active && !config.headers['x-portal-token']) config.headers['x-portal-token'] = active;
    return config;
});

/*
 * The guest's own session (guest role): one sign-in for their home page, every
 * group they are in, their meetings and their profile.
 *
 * A single slot, unlike the per-link store above, because it names the person
 * rather than a group. Kept under its own key, apart from the employee `token`,
 * for the same reason that store is.
 */
const GUEST_SESSION_KEY = 'guestSession';

export const getGuestSession = () => {
    try { return localStorage.getItem(GUEST_SESSION_KEY); } catch { return null; }
};

export const setGuestSession = (sessionToken) => {
    try { localStorage.setItem(GUEST_SESSION_KEY, sessionToken); } catch { /* lasts for the tab */ }
};

export const clearGuestSession = () => {
    try { localStorage.removeItem(GUEST_SESSION_KEY); } catch { /* nothing to clear */ }
};

/** The guest pages' requests. Reports failures back; never navigates by itself. */
export const guestApi = axios.create({
    baseURL: `${SERVER_URL}/api/portal`,
    headers: { 'Content-Type': 'application/json' }
});

guestApi.interceptors.request.use((config) => {
    const session = getGuestSession();
    if (session && !config.headers['x-portal-token']) config.headers['x-portal-token'] = session;
    return config;
});

export default portalApi;
